import React, { useEffect, useState } from 'react'

import { breakpoints } from '@components/layout/breakpoints.css'
import { GetSwimplyContactOptionsQuery } from '@libs/graphql/types'

export const useOutsideClick = (
  ref: React.MutableRefObject<any>,
  callback: () => void,
) => {
  const handleClick = React.useCallback(
    (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target)) {
        callback()
      }
    },
    [ref, callback],
  )

  React.useEffect(() => {
    document.addEventListener('click', handleClick)

    return () => {
      document.removeEventListener('click', handleClick)
    }
  })
}

type WindowDimentions = {
  width: number
  height: number
}

export function useWindowSize() {
  const [windowDimensions, setWindowDimensions] = useState<WindowDimentions>({
    width: 0,
    height: 0,
  })

  useEffect(() => {
    if (!window || typeof window === 'undefined') return

    function handleResize(): void {
      setWindowDimensions({
        width: window?.innerWidth,
        height: window?.innerHeight,
      })
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return (): void => window.removeEventListener('resize', handleResize)
  }, []) // Empty array ensures that effect is only run on mount

  return windowDimensions
}

export function useResponsive(
  breakpoint: keyof typeof breakpoints | 'always',
): boolean
export function useResponsive(): Record<keyof typeof breakpoints, boolean>
export function useResponsive(
  breakpoint?: keyof typeof breakpoints | 'always',
) {
  const { width: windowWidth } = useWindowSize()

  if (breakpoint) {
    if (breakpoint === 'always') return true

    const breakpointValue = breakpoints[breakpoint]
    if (typeof breakpointValue !== 'number') return true

    return windowWidth <= breakpointValue
  }

  return Object.keys(breakpoints).reduce(
    (acc, breakpoint) => {
      const breakpointValue = breakpoints[breakpoint]
      if (typeof breakpointValue !== 'number') return acc

      return {
        ...acc,
        [breakpoint]: windowWidth <= breakpointValue,
      }
    },
    {} as Record<keyof typeof breakpoints, boolean>,
  )
}

export const useWindowScroll = (threshold?: {
  x?: number
  y?: number
}): {
  isPastThreshold?: boolean
  position: { x: number; y: number }
} => {
  const [position, setPosition] = React.useState({ x: 0, y: 0 })
  const isPastThreshold = React.useMemo(
    () =>
      Boolean(
        (threshold?.x && position.x >= threshold.x) ||
        (threshold?.y && position.y >= threshold.y),
      ),
    [position.x, position.y, threshold?.x, threshold?.y],
  )

  const update = () =>
    setPosition({
      x: window.scrollX,
      y: window.scrollY,
    })

  React.useLayoutEffect(() => {
    window.addEventListener('scroll', update, false)

    return () => {
      window.removeEventListener('scroll', update, false)
    }
  }, [])

  return {
    isPastThreshold,
    position,
  }
}

export function usePrevious<T>(value: T): T | undefined {
  const ref = React.useRef<T>()
  React.useEffect(() => {
    ref.current = value
  })
  return ref.current ?? value
}

export function useMousePosition() {
  const [mousePosition, setMousePosition] = React.useState<{
    x: number
    y: number
  }>()

  const handleMouseMove = (e: MouseEvent) => {
    setMousePosition({
      x: e.pageX,
      y: e.pageY,
    })
  }

  React.useLayoutEffect(() => {
    window.addEventListener('mousemove', handleMouseMove)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  return mousePosition
}

export const useRefDimensions = (ref: React.RefObject<HTMLElement | null>) => {
  useWindowSize()

  // Reference: https://www.manuelkruisz.com/blog/posts/react-width-height-resize-hook
  const [width, setWidth] = React.useState(0)
  const [height, setHeight] = React.useState(0)

  React.useEffect(() => {
    if (!ref.current) {
      if (width !== 0) setWidth(0)
      if (height !== 0) setHeight(0)
      return
    }

    const { current } = ref
    const boundingRect = current.getBoundingClientRect()

    setWidth(Math.round(boundingRect.width))
    setHeight(Math.round(boundingRect.height))
  }, [height, ref, width])

  return React.useMemo(() => ({ width, height }), [width, height])
}

// Low enough to stop a runaway useEffect
// High enough to account for any re-rendering shenanigans
const DEFAULT_USE_EFFECT_RETRIES = 1000
/**
 * This hook allows you to execute a callback until that callback returns true
 * @deprecated Please use a normal useEffect OR find a way to not use a useEffect at all.
 * @param callback - A callback to trigger which returns a boolean. The return value of the callback determines whether the callback should continue to fire or not. Return true when the callback should stop firing, false when it should continue.
 * @param options - options for the useEffect.
 */
export function useEffectUntil(
  callback: () => boolean | undefined,
  options?: {
    retry?: number
  },
) {
  const mounted = React.useRef(false)
  const maxRetries = options?.retry ?? DEFAULT_USE_EFFECT_RETRIES
  const retries = React.useRef(0)

  React.useEffect(() => {
    if (mounted.current) return
    if (retries.current >= maxRetries) return
    mounted.current = callback() ?? false
    retries.current += 1
  }, [callback, maxRetries])
}

function isSingleChatOption(
  contactOptions:
    | GetSwimplyContactOptionsQuery['swimplyContactOptions']
    | undefined
    | null,
) {
  if (contactOptions == null) return false
  return (
    contactOptions.options.length === 1 &&
    contactOptions.options[0].type === 'CHAT'
  )
}

export function openSingleChatOption(
  contactOptions:
    | GetSwimplyContactOptionsQuery['swimplyContactOptions']
    | undefined
    | null,
  openChat: () => void,
) {
  if (!isSingleChatOption(contactOptions)) return false

  openChat()
  return true
}

export const hookUtils = {
  useOutsideClick,
  useWindowSize,
  useWindowScroll,
  usePrevious,
  useMousePosition,
  useRefDimensions,
  useEffectUntil,
  useResponsive,
  openSingleChatOption,
}
